'use client';

import {
  SearchEngine,
  buildUrlManager,
  loadQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/atomic-react';
import { useEffect, useRef } from 'react';

/**
 * Translates legacy CategoryFacet URLs (`cf-{field}={seg1},{seg2}`) to the
 * flat Facet format (`f-{field}={encoded "seg1 > seg2"}`) that buildFacet uses.
 *
 * Example: `cf-ec_category=3d,characters` → `f-ec_category=3d%20%3E%20characters`
 */
function translateCfFragment(fragment: string): string {
  return fragment.replace(/\bcf-([^=&]+)=([^&]*)/g, (_, field, rawValue) => {
    try {
      const value = decodeURIComponent(rawValue);
      const flatValue = value
        .split(',')
        .map((s) => s.trim())
        .join(' > ');
      return `f-${field}=${encodeURIComponent(flatValue)}`;
    } catch {
      return `f-${field}=${rawValue}`;
    }
  });
}

/**
 * Wires up Coveo's UrlManager to the browser's history API and handles
 * back/forward navigation. Dispatches the initial search on first call.
 *
 * @param eng - The Coveo engine to sync. Pass null when not yet initialized.
 * @param readLocalStorage - When true, falls back to coveo-standalone-search-box-data
 *   in localStorage to seed the initial query (used on the /search route when
 *   arriving from the header search box and there is no URL hash present).
 *   The `q` parameter from the URL hash is always applied regardless of this flag.
 */
export function useUrlManagerSync(
  eng: SearchEngine | null,
  readLocalStorage = false
): void {
  const currentHash = useRef('');

  useEffect(() => {
    if (!eng) return;

    let initialFragment = translateCfFragment(window.location.hash.slice(1));

    if (!initialFragment && readLocalStorage) {
      try {
        const stored = localStorage.getItem('coveo-standalone-search-box-data');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.value) {
            initialFragment = `q=${encodeURIComponent(data.value)}`;
          }
          localStorage.removeItem('coveo-standalone-search-box-data');
        }
      } catch {
        // ignore malformed data
      }
    }

    currentHash.current = initialFragment;

    const urlManager = buildUrlManager(eng, {
      initialState: { fragment: initialFragment },
    });

    let firstSearchDone = false;
    let isRestoringFromPopState = false;

    const unsubscribe = urlManager.subscribe(() => {
      const fragment = urlManager.state.fragment;

      if (!firstSearchDone) {
        if (fragment) {
          firstSearchDone = true;
        } else if (initialFragment) {
          return;
        }
      }

      const newHash = fragment ? `#${fragment}` : '';
      currentHash.current = fragment;

      if (newHash !== window.location.hash) {
        const fullUrl = `${window.location.pathname}${window.location.search}${newHash}`;
        if (isRestoringFromPopState) {
          window.history.replaceState(null, '', fullUrl);
        } else {
          window.history.pushState(null, '', fullUrl);
        }
      }
    });

    function syncAndSearch(hash: string) {
      isRestoringFromPopState = true;
      urlManager.synchronize(hash);
      const analyticsAction = loadSearchAnalyticsActions(
        eng!
      ).logSearchFromLink();
      eng!.dispatch(loadSearchActions(eng!).executeSearch(analyticsAction));
      currentHash.current = hash;
      setTimeout(() => {
        isRestoringFromPopState = false;
      }, 0);
    }

    const onPopState = () => {
      const hash = window.location.hash.slice(1);
      try {
        if (
          decodeURIComponent(currentHash.current) !== decodeURIComponent(hash)
        ) {
          syncAndSearch(hash);
        }
      } catch {
        if (currentHash.current !== hash) {
          syncAndSearch(hash);
        }
      }
    };

    window.addEventListener('popstate', onPopState);

    if (initialFragment) {
      const q = new URLSearchParams(initialFragment).get('q');
      if (q) {
        eng.dispatch(loadQueryActions(eng).updateQuery({ q }));
      }
    }

    const analyticsAction = loadSearchAnalyticsActions(eng).logInterfaceLoad();
    eng.dispatch(loadSearchActions(eng).executeSearch(analyticsAction));

    return () => {
      unsubscribe();
      window.removeEventListener('popstate', onPopState);
    };
  }, [eng, readLocalStorage]);
}
